import { PlanInterval } from '@aibees/shared/definition';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { usePaymentStatus } from '@aibees/shared/hooks/payment/use-payment-status';
import { classnames, fDate } from '@aibees/shared/utils';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { CurrentPlanInfoProps as Props } from './types';

export const CurrentPlanInfo = memo(({ className }: Props) => {
  const { data } = useCurrentSubscriptionApi();
  const { paymentFailed } = usePaymentStatus();

  const isCurrentPlanFree = data?.current?.plan?.metadata.is_free;

  const isSpecialRate = useMemo(
    () => data?.current?.discounts && data?.current?.discounts?.length > 0,
    [data]
  );

  return (
    <Styled.Wrapper
      className={classnames('CurrentPlanInfo', className)}
      sx={SX.root}
      direction="row"
      justifyContent="space-between"
      alignItems="flex-end"
    >
      <Stack gap={7}>
        <Typography variant="body3" fontWeight={700} color="black">
          {isCurrentPlanFree ? 'Free plan' : 'Usage Based-Plan'}
        </Typography>
        <Stack direction="row" gap={0.5} sx={SX.planPeriod}>
          {data?.current?.cancelAtPeriodEnd
            ? 'Active until '
            : 'Next Charge in '}
          {fDate(data?.current?.currentPeriodEnd)}
          {paymentFailed && (
            <Typography variant="body4" color="error.500">
              (Pending on Payment)
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack sx={SX.rightBox}>
        {data?.next ? (
          <Stack direction="row" sx={SX.upcoming} gap={3} alignItems="center">
            <Typography variant="body4" fontWeight={700} color="warning.600">
              Upcoming Plan
            </Typography>
            <Stack>
              <Typography variant="body4" fontWeight={400} color="warning.700">
                Effective on {fDate(data?.next?.startDate, 'dd/MM/yy')}
              </Typography>
              <Typography variant="body4" fontWeight={700} color="warning.700">
                {data?.next?.plan?.metadata.request} / Free Plan
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Box />
        )}
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Chip
            variant="outlined"
            color="primary"
            size="small"
            label={`${
              data?.current?.plan?.interval === PlanInterval.MONTH
                ? 'Monthly'
                : 'Yearly'
            } / $${data?.current?.amount}`}
          />
          {isSpecialRate && (
            <Chip
              variant="outlined"
              color="primary"
              label="Special Rate"
              size="small"
            />
          )}
        </Stack>
      </Stack>
    </Styled.Wrapper>
  );
});

CurrentPlanInfo.displayName = 'CurrentPlanInfo';
